import type React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import CustomTextField from "../../../../components/ui/Textfield";

export interface RegistryRole {
  id: number;
  name: string;
  description?: string;
}

interface Props {
  open: boolean;
  role: RegistryRole | null;
  onClose: () => void;
  onSaved: () => void;
}

const RoleFormDialog: React.FC<Props> = ({ open, role, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(role?.name ?? "");
      setDescription(role?.description ?? "");
    }
  }, [open, role]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() };
      if (role) {
        await apiService.patch(`/api/crews/roles/${role.id}/`, body);
        toast.success("Role updated.");
      } else {
        await apiService.post("/api/crews/roles/", body);
        toast.success("Role created.");
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      const data = (e as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      const msg =
        (data && typeof data === "object"
          ? Object.values(data).flat().join(" ")
          : "") || "Failed to save role.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {role ? "Edit Role" : "New Role"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <CustomTextField
          label="Role name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <CustomTextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={3}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            textTransform: "none",
            backgroundColor: "#6b4226",
            "&:hover": { backgroundColor: "#5a3720" },
          }}
        >
          {saving ? "Saving..." : role ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormDialog;
